/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { useEffect, useState } from 'react';
import * as _ from 'lodash';
import { Duration } from 'luxon';
import { css, StyleSheet } from 'aphrodite';

import { getConfig } from 'blb-utils/config';
import { parseLastActivityCookie } from 'blb-utils/cookies';
import { ActionButton, DomEventListener, Modal, Text } from '@blend/enterprise-components';
import { ProvideTimers, TimerProps, humanizeHoursAndMinutesDuration } from '@blend/shared-react';

import { ModalContent } from 'blb-components/ModalContent';
import { ping, touch, logout } from 'blb-api/auth';

const styles = StyleSheet.create({
  buttonGroup: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: 24,
  },
  UserIconButton: {
    marginRight: 8,
  },
});

const PING_INTERVAL_MS = 30 * 1000; // 30 seconds;
const ONE_MINUTE_MS = 60 * 1000;

export type InactivityLogoutProps = TimerProps;

const InactivityLogout = ({ reactSafeSetInterval }: InactivityLogoutProps) => {
  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);
  const [lastActiveUtc, setLastActiveUtc] = useState<number>(parseLastActivityCookie());

  const { sessionLimits } = getConfig();
  const { logoutWarningTimeMs, maxSessionTimeMs } = sessionLimits;
  const warningTimeMinutes = Duration.fromObject({ millisecond: logoutWarningTimeMs }).as('minutes');
  const maxIdleTimeMinutes = Duration.fromObject({ millisecond: maxSessionTimeMs }).as('minutes');
  const warningWindow = maxSessionTimeMs - logoutWarningTimeMs;

  const touchAndSetLastActive = async () => {
    await touch();
    setLastActiveUtc(parseLastActivityCookie());
  };

  const stayLoggedIn = async () => {
    activityDetectedHandler.flush();
    await touchAndSetLastActive();
    setIsWarningModalOpen(false);
  };

  const activityDetectedHandler = _.throttle(touchAndSetLastActive, ONE_MINUTE_MS, { leading: true, trailing: false });

  useEffect(() => {
    const checkSession = async () => {
      // check if the session has expired (will logout if so)
      await ping();

      const timeSinceLastActivity = Date.now() - lastActiveUtc;

      if (timeSinceLastActivity >= warningWindow) setIsWarningModalOpen(true);
    };

    const pingInterval = reactSafeSetInterval(checkSession, PING_INTERVAL_MS);

    return () => window.clearInterval(pingInterval);
  }, [lastActiveUtc]);

  return (
    <>
      <DomEventListener cb={activityDetectedHandler} eventName="keydown" target={document} />
      <DomEventListener cb={activityDetectedHandler} eventName="mousemove" target={document} />
      <Modal contentLabel="Extend Session Modal" handleClose={logout} isOpen={isWarningModalOpen}>
        <ModalContent headerText="Are you still there?">
          <Text>
            For your security, your session times out after {humanizeHoursAndMinutesDuration(maxIdleTimeMinutes)} of
            inactivity. Your session will end in {humanizeHoursAndMinutesDuration(warningTimeMinutes)} unless you choose
            to stay logged in.
          </Text>
          <div className={css(styles.buttonGroup)}>
            <div className={css(styles.UserIconButton)}>
              <ActionButton label="Log me out" onClick={logout} theme="tertiary" />
            </div>
            <ActionButton label="Stay logged in" onClick={stayLoggedIn} theme="primary" />
          </div>
        </ModalContent>
      </Modal>
    </>
  );
};

export const InactivityTimer = () => {
  return <ProvideTimers render={timerProps => <InactivityLogout {...timerProps} />} />;
};
